import { useEffect, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import Sidebar from '../components/layout/sidebar/sidebar'
import { MainView } from '../components/layout/main'
import { ClientsWithClaimsStat } from '../components/reusable'
import api from '../helpers/apiRequester'
import { getLoginUrl } from '../helpers/authConfig'
import { StatsView } from '../components/views'
import styles from './pcp-referral-engagement-stats.module.scss'
import { Page, PageHeader, Section, Button } from '../ui'

const PAGE_SIZE = 25

interface MemberRow {
    agent: string
    email: string
    sentToday: number
    sentTotal: number
    signedToday: number
    signedTotal: number
}

function PcpReferralTeamLeaderStats() {
    const [dataReady, setDataReady] = useState(false)
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<any>()

    useEffect(() => {
        if (!dataReady && !loading) {
            setLoading(true)
            getData()
        }
    }, [dataReady, loading])

    const getData = async () => {
        Loading.circle('Loading...')
        try {
            const res = await api({ url: '/pcp-referral-team-leader-stats' })
            setData(res.data)
            setDataReady(true)
            Loading.remove()
        } catch (error: any) {
            console.log(error)
            Loading.remove()
            if (error?.response?.status === 403) {
                Report.failure(
                    'Access Denied',
                    'This page is only available to team leaders.',
                    'Okay'
                )
            } else if (error?.response?.status === 401) {
                Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                window.location.href = getLoginUrl()
            }
        }
    }

    const teamName: string | undefined = data?.team?.name
    const members: MemberRow[] = data?.ats || []

    return (
        <div className="home">
            <Sidebar />
            <MainView>
                <Page>
                    <PageHeader
                        eyebrow="PCP Referral"
                        title="My Team Stats"
                        meta={teamName ? <span>{teamName}</span> : undefined}
                    />
                    <div>
                        {data?.figures && <StatsView stats={data.figures} />}
                        {data && (
                            <ClientsWithClaimsStat
                                title={teamName ? `Clients with Claims — ${teamName}` : 'Clients with Claims'}
                                count={data?.clientsWithClaims}
                                subtitle="Clients your team has signed who have at least one policy-bearing claim (this campaign). Download the full list — client name, signed date/time and signing agent."
                                endpoint="/pcp-referral-team-claims-report"
                                requestBody={{}}
                                filenamePrefix={`team-clients-with-claims-${teamName || 'team'}`}
                            />
                        )}
                        {data?.ats && (
                            <div className={styles.statsSection}>
                                <Section
                                    title="Team Members"
                                    actions={
                                        <Button
                                            variant="neutral"
                                            size="sm"
                                            onClick={() =>
                                                downloadCsv(
                                                    members.map((r) => ({
                                                        Member: r.agent,
                                                        'Sent Today': r.sentToday,
                                                        'Sent Total': r.sentTotal,
                                                        'Signed Today': r.signedToday,
                                                        'Signed Total': r.signedTotal,
                                                    })),
                                                    `team-stats-${teamName || 'team'}`
                                                )
                                            }
                                        >
                                            Download CSV
                                        </Button>
                                    }
                                >
                                    <MemberStatsTable rows={members} />
                                </Section>
                            </div>
                        )}
                    </div>
                </Page>
            </MainView>
        </div>
    )
}

export default PcpReferralTeamLeaderStats

// ─── CSV export helper ───────────────────────────────────────────────────────

const downloadCsv = (rows: Record<string, any>[], filename: string) => {
    if (!rows.length) return
    const headers = Object.keys(rows[0])
    const escape = (v: any) => {
        const s = String(v ?? '')
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
    }
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// ─── Member stats table (paginated) ──────────────────────────────────────────

const MemberStatsTable = ({ rows }: { rows: MemberRow[] }) => {
    const [page, setPage] = useState(1)
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages)
    const start = (safePage - 1) * PAGE_SIZE
    const pageRows = rows.slice(start, start + PAGE_SIZE)

    if (!rows.length) return <p className={styles.emptyText}>No team members found.</p>

    return (
        <div className={styles.tableWrapper}>
            <table className={styles.statsTable}>
                <thead>
                    <tr>
                        <th>Member</th>
                        <th className={styles.center}>Sent Today</th>
                        <th className={styles.center}>Sent Total</th>
                        <th className={styles.center}>Signed Today</th>
                        <th className={styles.center}>Signed Total</th>
                    </tr>
                </thead>
                <tbody>
                    {pageRows.map((r, i) => (
                        <tr key={i}>
                            <td>{r.agent}</td>
                            <td className={styles.center}>{r.sentToday}</td>
                            <td className={styles.center}>{r.sentTotal}</td>
                            <td className={styles.center}>{r.signedToday}</td>
                            <td className={styles.center}>{r.signedTotal}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {totalPages > 1 && (
                <div className={styles.paginationSection}>
                    <span className={styles.paginationInfo}>
                        Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length} members
                    </span>
                    <div className={styles.paginationControls}>
                        <button
                            className={styles.paginationButton}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={safePage === 1}
                        >
                            ‹
                        </button>
                        <Pagination current={safePage} total={totalPages} onChange={setPage} />
                        <button
                            className={styles.paginationButton}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Pagination helper ───────────────────────────────────────────────────────

const Pagination = ({ current, total, onChange }: { current: number; total: number; onChange: (p: number) => void }) => {
    const pages: (number | '...')[] = []
    if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
    } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
    }
    return (
        <>
            {pages.map((p, i) =>
                p === '...' ? (
                    <span key={`e-${i}`} className={styles.paginationEllipsis}>
                        …
                    </span>
                ) : (
                    <button
                        key={p}
                        onClick={() => onChange(p)}
                        className={`${styles.paginationButton} ${current === p ? styles.paginationButtonActive : ''}`}
                    >
                        {p}
                    </button>
                )
            )}
        </>
    )
}
